import { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, parseISO } from 'date-fns';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, sendPaymentReminderNow, getPaymentReminderTemplates } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
  Clock, Bell, Repeat, Trash2, DollarSign, Users, Target, X, Send, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const eventTypeIcons = {
  general: CalendarIcon,
  payment_due: DollarSign,
  meeting: Users,
  follow_up: Target,
  deadline: Clock,
};

const eventColors = {
  cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CalendarView = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const res = await getCalendarEvents(start.toISOString(), end.toISOString());
      setEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch events', err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = [];
    
    // Add padding for days before start of month
    const startDay = start.getDay();
    for (let i = 0; i < startDay; i++) {
      days.push({ date: null, events: [] });
    }
    
    // Add days of month
    let current = new Date(start);
    while (current <= end) {
      const dayEvents = events.filter(e => {
        const eventDate = typeof e.start_date === 'string' ? parseISO(e.start_date) : e.start_date;
        return isSameDay(eventDate, current);
      });
      days.push({ date: new Date(current), events: dayEvents });
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const handleDayClick = (day) => {
    if (day.date) {
      setSelectedDate(day.date);
      setSelectedEvent(null);
      setShowEventModal(true);
    }
  };

  const handleEventClick = (e, event) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setSelectedDate(parseISO(event.start_date));
    setShowEventModal(true);
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const days = getDaysInMonth();

  return (
    <div className="h-full flex flex-col bg-background p-4" data-testid="calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-accent" />
            Calendar
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-lg font-semibold min-w-[150px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <Button variant="ghost" size="icon" onClick={handleNextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <Button 
          onClick={() => { setSelectedDate(new Date()); setSelectedEvent(null); setShowEventModal(true); }}
          className="bg-accent hover:bg-accent/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Event
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 border rounded-lg overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b bg-muted/30">
          {DAYS.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 flex-1" style={{ gridAutoRows: '1fr' }}>
          {days.map((day, idx) => (
            <div
              key={idx}
              onClick={() => handleDayClick(day)}
              className={`
                border-b border-r p-1 min-h-[100px] cursor-pointer transition-colors
                ${!day.date ? 'bg-muted/10' : 'hover:bg-muted/30'}
                ${day.date && isSameDay(day.date, new Date()) ? 'bg-accent/10' : ''}
              `}
            >
              {day.date && (
                <>
                  <div className={`text-sm font-medium mb-1 ${isSameDay(day.date, new Date()) ? 'text-accent' : ''}`}>
                    {format(day.date, 'd')}
                  </div>
                  <div className="space-y-1">
                    {day.events.slice(0, 3).map(event => {
                      const Icon = eventTypeIcons[event.event_type] || CalendarIcon;
                      return (
                        <div
                          key={event.id || event.instance_id}
                          onClick={(e) => handleEventClick(e, event)}
                          className={`
                            text-xs p-1 rounded truncate flex items-center gap-1 border
                            ${eventColors[event.color] || eventColors.cyan}
                          `}
                        >
                          <Icon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{event.title}</span>
                          {event.is_recurring && <Repeat className="w-3 h-3 flex-shrink-0" />}
                        </div>
                      );
                    })}
                    {day.events.length > 3 && (
                      <div className="text-xs text-muted-foreground text-center">
                        +{day.events.length - 3} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Event Modal */}
      <EventModal
        open={showEventModal}
        onClose={() => setShowEventModal(false)}
        selectedDate={selectedDate}
        event={selectedEvent}
        onSaved={fetchEvents}
      />
    </div>
  );
};

const EventModal = ({ open, onClose, selectedDate, event, onSaved }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'general',
    start_date: '',
    all_day: false,
    is_recurring: false,
    recurrence_type: 'monthly',
    recurrence_interval: 1,
    recurrence_day_of_month: 1,
    notify_email: true,
    notify_sms: false,
    notify_before_minutes: 60,
    color: 'cyan',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderEmail, setReminderEmail] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('preset_friendly');

  useEffect(() => {
    // Fetch templates when component mounts
    const fetchTemplates = async () => {
      try {
        const res = await getPaymentReminderTemplates();
        setTemplates(res.data.filter(t => t.template_type === 'preset'));
      } catch (err) {
        console.error('Failed to fetch templates');
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || 'general',
        start_date: event.start_date ? format(parseISO(event.start_date), "yyyy-MM-dd'T'HH:mm") : '',
        all_day: event.all_day || false,
        is_recurring: event.is_recurring || false,
        recurrence_type: event.recurrence_type || 'monthly',
        recurrence_interval: event.recurrence_interval || 1,
        recurrence_day_of_month: event.recurrence_day_of_month || 1,
        notify_email: event.notify_email ?? true,
        notify_sms: event.notify_sms || false,
        notify_before_minutes: event.notify_before_minutes || 60,
        color: event.color || 'cyan',
      });
    } else if (selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd'T'09:00");
      setFormData(prev => ({
        ...prev,
        title: '',
        description: '',
        start_date: dateStr,
        recurrence_day_of_month: selectedDate.getDate(),
      }));
    }
  }, [event, selectedDate]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Event title is required');
      return;
    }
    if (!formData.start_date) {
      toast.error('Start date is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
      };

      if (event && !event.is_instance) {
        await updateCalendarEvent(event.id, payload);
        toast.success('Event updated');
      } else {
        await createCalendarEvent(payload);
        toast.success('Event created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event || event.is_instance) return;
    if (!window.confirm('Delete this event? This will also delete all future occurrences if recurring.')) return;

    setDeleting(true);
    try {
      await deleteCalendarEvent(event.id);
      toast.success('Event deleted');
      onSaved();
      onClose();
    } catch (err) {
      toast.error('Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? 'Edit Event' : 'New Event'}</DialogTitle>
          <DialogDescription>
            {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>Event Title *</Label>
            <Input
              placeholder="e.g., ABC Construction owes $500"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              data-testid="event-title-input"
            />
          </div>

          {/* Event Type - Visual Buttons */}
          <div className="space-y-2">
            <Label>What is this for?</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, event_type: 'payment_due', color: 'green' })}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  formData.event_type === 'payment_due' 
                    ? 'border-green-500 bg-green-500/20 text-green-400' 
                    : 'border-muted hover:border-muted-foreground/50'
                }`}
              >
                <DollarSign className="w-5 h-5" />
                <span className="text-xs font-medium">Payment Due</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, event_type: 'meeting', color: 'purple' })}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  formData.event_type === 'meeting' 
                    ? 'border-purple-500 bg-purple-500/20 text-purple-400' 
                    : 'border-muted hover:border-muted-foreground/50'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs font-medium">Meeting</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, event_type: 'deadline', color: 'red' })}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  formData.event_type === 'deadline' 
                    ? 'border-red-500 bg-red-500/20 text-red-400' 
                    : 'border-muted hover:border-muted-foreground/50'
                }`}
              >
                <Clock className="w-5 h-5" />
                <span className="text-xs font-medium">Deadline</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, event_type: 'follow_up', color: 'orange' })}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  formData.event_type === 'follow_up' 
                    ? 'border-orange-500 bg-orange-500/20 text-orange-400' 
                    : 'border-muted hover:border-muted-foreground/50'
                }`}
              >
                <Target className="w-5 h-5" />
                <span className="text-xs font-medium">Follow Up</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, event_type: 'general', color: 'cyan' })}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  formData.event_type === 'general' 
                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400' 
                    : 'border-muted hover:border-muted-foreground/50'
                }`}
              >
                <CalendarIcon className="w-5 h-5" />
                <span className="text-xs font-medium">General</span>
              </button>
            </div>
          </div>

          {/* Date/Time */}
          <div className="space-y-2">
            <Label>Date & Time *</Label>
            <Input
              type="datetime-local"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>

          {/* Recurring */}
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-accent" />
                <Label className="font-medium">Recurring Event</Label>
              </div>
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(v) => setFormData({ ...formData, is_recurring: v })}
              />
            </div>

            {formData.is_recurring && (
              <div className="space-y-3 pt-2 border-t">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Repeat Every</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={formData.recurrence_interval}
                        onChange={(e) => setFormData({ ...formData, recurrence_interval: parseInt(e.target.value) || 1 })}
                        className="w-16"
                      />
                      <Select value={formData.recurrence_type} onValueChange={(v) => setFormData({ ...formData, recurrence_type: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Day(s)</SelectItem>
                          <SelectItem value="weekly">Week(s)</SelectItem>
                          <SelectItem value="monthly">Month(s)</SelectItem>
                          <SelectItem value="yearly">Year(s)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {formData.recurrence_type === 'monthly' && (
                    <div className="space-y-1">
                      <Label className="text-xs">On Day</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.recurrence_day_of_month}
                        onChange={(e) => setFormData({ ...formData, recurrence_day_of_month: parseInt(e.target.value) || 1 })}
                        placeholder="Day of month"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-4 h-4 text-accent" />
              <Label className="font-medium">Notifications</Label>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Email Reminder</span>
              <Switch
                checked={formData.notify_email}
                onCheckedChange={(v) => setFormData({ ...formData, notify_email: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">SMS Reminder</span>
              <Switch
                checked={formData.notify_sms}
                onCheckedChange={(v) => setFormData({ ...formData, notify_sms: v })}
              />
            </div>
            {(formData.notify_email || formData.notify_sms) && (
              <div className="space-y-1">
                <Label className="text-xs">Remind Before</Label>
                <Select 
                  value={String(formData.notify_before_minutes)} 
                  onValueChange={(v) => setFormData({ ...formData, notify_before_minutes: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="1440">1 day</SelectItem>
                    <SelectItem value="10080">1 week</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              placeholder="Additional details..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          {/* Quick Send Reminder - Only for payment_due events that already exist */}
          {event && formData.event_type === 'payment_due' && (
            <div className="space-y-3 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-green-400" />
                  <Label className="font-medium text-green-400">Send Payment Reminder</Label>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReminderForm(!showReminderForm)}
                  className="text-green-400"
                >
                  {showReminderForm ? 'Hide' : 'Quick Send'}
                </Button>
              </div>
              
              {showReminderForm && (
                <div className="space-y-3 pt-2 border-t border-green-500/20">
                  <div className="space-y-1">
                    <Label className="text-xs">Send to email</Label>
                    <Input
                      type="email"
                      placeholder="client@email.com"
                      value={reminderEmail}
                      onChange={(e) => setReminderEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Template</Label>
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={async () => {
                      if (!reminderEmail) {
                        toast.error('Please enter an email address');
                        return;
                      }
                      setSendingReminder(true);
                      try {
                        await sendPaymentReminderNow({
                          template_id: selectedTemplate,
                          recipient_name: formData.title.replace(' Payment Due', '').replace('Payment Due', 'Client'),
                          recipient_email: reminderEmail,
                          due_date: formData.start_date ? format(parseISO(formData.start_date), 'MMMM d, yyyy') : null,
                          scheduled_for: new Date().toISOString(),
                        });
                        toast.success(`Reminder sent to ${reminderEmail}!`);
                        setShowReminderForm(false);
                        setReminderEmail('');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to send');
                      } finally {
                        setSendingReminder(false);
                      }
                    }}
                    disabled={sendingReminder}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingReminder ? 'Sending...' : 'Send Reminder Now'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            {event && !event.is_instance && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-accent hover:bg-accent/90">
              {saving ? 'Saving...' : (event ? 'Update' : 'Create Event')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarView;
